import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { PaywallDialog } from '@/components/PaywallDialog';
import { handleNavigation } from './navigation-utils';
import { routePathByName } from '@/app/routes';

// Global state for PaywallDialog visibility
let isPaywallDialogOpen = false;
let setPaywallDialogOpen: ((open: boolean) => void) | null = null;

// Function to open the paywall dialog from anywhere
export const openPaywallDialog = (): void => {
  if (setPaywallDialogOpen) {
    setPaywallDialogOpen(true);
    isPaywallDialogOpen = true;
  }
};

// PaywallDialogProvider component to be included in the app root
export const PaywallDialogProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  
  // Store the setter in our global variable
  React.useEffect(() => {
    setPaywallDialogOpen = setDialogOpen;
    return () => { setPaywallDialogOpen = null; };
  }, []);

  return (
    <>
      {children}
      <PaywallDialog 
        open={dialogOpen} 
        onOpenChange={setDialogOpen} 
      />
    </>
  );
};

/**
 * Check if current route is the Essential Eight route that needs paywall protection
 */
export const isEssentialEightRoute = (): boolean => {
  return window.location.pathname === '/essential-eight';
};

/**
 * Handle navigation with paywall checking
 * Shows paywall dialog instead of navigating if on Essential Eight route
 */
export const handleNavigationWithPaywall = (
  url: string,
  navigate: NavigateFunction,
  e?: React.MouseEvent,
  routeName?: string,
  params?: Record<string, string>
): void => {
  // Check if this is a middle click (button 1) or right click (button 2)
  const isMiddleOrRightClick = e && (e.button === 1 || e.button === 2);

  // Always allow dashboard and external links
//   if (url === '/dashboard' || url === '#' || url.startsWith('http')) {
//     handleNavigation(url, navigate, e, routeName, params);
//     return null;
//   }

  // On Essential Eight route, show paywall dialog (but allow middle/right clicks to open new tabs)
  if (isEssentialEightRoute() && !isMiddleOrRightClick) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Show the dialog by calling our global handler
    openPaywallDialog();

    return;
  }

  // For middle/right clicks, open in new tab regardless of paywall
  if (isMiddleOrRightClick && e) {
    e.preventDefault();
    e.stopPropagation();

    // Handle navigation with new tab
    let targetUrl = url;
    if (routeName) {
      const resolved = routePathByName(routeName, params);
      if (resolved && resolved !== '/not-found') {
        targetUrl = resolved;
      }
    }

    // Open in new tab
    if (targetUrl.startsWith('http') || targetUrl.startsWith('https')) {
      window.open(targetUrl, '_blank');
    } else if (targetUrl !== '#') {
      window.open(window.location.origin + targetUrl, '_blank');
    }
    return;
  }

  // Normal navigation on other routes
  handleNavigation(url, navigate, e, routeName, params);
};

